import React, { useEffect, Fragment, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import DataTable from "react-data-table-component";
import { CSVLink } from "react-csv";
import { showToast } from "../../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import Swal from "sweetalert2";
import Breadcrumb from "../../../../components/common/breadcrumb";
import { DeleteIpdEntry, GetIpdEntries } from "../../../../api/ipd/ipdEntryAPI";
import { DeletePaymentReceive, GetPaymentReceives } from "../../../../api/ipd/paymentReceiveAPI";

const ListPaymentReceiveEntry = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchPaymentReceives = async () => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetPaymentReceives(); // Your API should return a list of OPD entries
      if (response.success) {
        showToast("success", response.message);
        const formattedData = response.data.map((item, index) => ({
          index: index + 1,
          id: item._id,
          type: item.type || "",
          subSpeciality: item.subSpeciality || "",
          receiptNo: item.receiptNo || "",
          labNo: item.labNo || "",
          date: item.date ? new Date(item.date).toLocaleDateString() : "",
          time: item.time || "",
          ipdNo: item.ipdEntryId?.patientId?.ipdNo || "",
          patientName:
            (item.ipdEntryId?.patientId?.patientNamePrefix ? item.ipdEntryId?.patientId?.patientNamePrefix + " " : "") +
            (item.ipdEntryId?.patientId?.patientName || ""),
          sex: item.ipdEntryId?.patientId?.sex || "",
          age: item.ipdEntryId?.patientId?.age ? `${item.ipdEntryId?.patientId?.age} ${item.ipdEntryId?.patientId?.ageSuffix || ""}` : "",
          mobileNo: item.ipdEntryId?.patientId?.mobileNo || "",
          address: item.ipdEntryId?.patientId?.address || "",
		  paymentMode: item.paymentMode || "",
		  amount: item.amount || "",
		  remark: item.remark || "",
		  debitAccount: item.debitAccount || ""
        }));
        setData(formattedData);
        setFilteredData(formattedData);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error.message);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  useEffect(() => {
    fetchPaymentReceives();
  }, []);

  useEffect(() => {
    const results = data.filter(item =>
      Object.values(item)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
    setFilteredData(results);
  }, [search, data]);

  const handleEditClick = id => {
    navigate(`/ipd/transaction/payment-receive-entry/edit/${id}`);
  };

  const handleViewClick = id => {
    navigate(`/ipd/transaction/payment-receive-entry/view/${id}`);
  };

  const handleDeleteClick = async id => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then(async result => {
      if (result.isConfirmed) {
        dispatch({ type: "LOADER_START" });
        try {
          const response = await DeletePaymentReceive(id);
          if (response.success) {
            showToast("success", response.message);
            const newData = data.filter(item => item.id !== id);
            setData(newData);
            setFilteredData(newData);
          } else {
            showToast("error", response.message);
          }
        } catch (error) {
          showToast("error", error.message);
        } finally {
          dispatch({ type: "LOADER_STOP" });
        }
      }
    });
  };

  const columns = [
    { name: "S.No", selector: row => row.index, sortable: true, width: "8%" },
	{ name: "Type", selector: row => row.type, sortable: true, width: "8%" },
	{ name: "Sub Speciality", selector: row => row.subSpeciality, sortable: true, width: "8%" },
	{ name: "Receipt No", selector: row => row.receiptNo, sortable: true, width: "8%" },
	{ name: "Lab No", selector: row => row.labNo, sortable: true, width: "8%" },
	{ name: "Date", selector: row => row.date, sortable: true, width: "8%" },
	{ name: "Time", selector: row => row.time, sortable: true, width: "8%" },
	{ name: "Ipd No", selector: row => row.ipdNo, sortable: true, width: "8%" },
	{ name: "Patient Name", selector: row => row.patientName, sortable: true, width: "8%" },
	{ name: "Sex", selector: row => row.sex, sortable: true, width: "8%" },
	{ name: "Age", selector: row => row.age, sortable: true, width: "8%" },
	{ name: "Mobile No", selector: row => row.mobileNo, sortable: true, width: "8%" },
	{ name: "Address", selector: row => row.address, sortable: true, width: "8%" },
	{ name: "Payment Mode", selector: row => row.paymentMode, sortable: true, width: "8%" },
	{ name: "Amount", selector: row => row.amount, sortable: true, width: "8%" },
	{ name: "Remark", selector: row => row.remark, sortable: true, width: "8%" },
	{ name: "Debit Account", selector: row => row.debitAccount, sortable: true, width: "8%" },
    {
      name: "Actions",
      cell: row => (
        <div>
          <button
              className="btn btn-info me-2"
              onClick={() => handleViewClick(row.id)}
              id={`view-${row.id}`}
            >
              <i className="fa fa-eye" />
            </button>
            <UncontrolledTooltip placement="top" target={`view-${row.id}`}>
              View
            </UncontrolledTooltip>
          <button
            className="btn btn-primary me-2"
            onClick={() => handleEditClick(row.id)}
            id={`edit-${row.id}`}
          >
            <i className="fa fa-pencil-square-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`edit-${row.id}`}>
            Edit
          </UncontrolledTooltip>

          <button
            className="btn btn-danger"
            onClick={() => handleDeleteClick(row.id)}
            id={`delete-${row.id}`}
          >
            <i className="fa fa-trash-o" />
          </button>
          <UncontrolledTooltip placement="top" target={`delete-${row.id}`}>
            Delete
          </UncontrolledTooltip>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "20%"
    }
  ];

  const headers = [
    { label: "S.No", key: "index" },
	{ label: "Type", key: "type" },
	{ label: "Sub Speciality", key: "subSpeciality" },
	{ label: "Receipt No", key: "receiptNo" },
	{ label: "Lab No", key: "labNo" },
	{ label: "Date", key: "date" },
	{ label: "Time", key: "time" },
	{ label: "Ipd No", key: "ipdNo" },
	{ label: "Patient Name", key: "patientName" },
	{ label: "Sex", key: "sex" },
	{ label: "Age", key: "age" },
	{ label: "Mobile No", key: "mobileNo" },
	{ label: "Address", key: "address" },
	{ label: "Payment Mode", key: "paymentMode" },
	{ label: "Amount", key: "amount" },
	{ label: "Remark", key: "remark" },
	{ label: "Debit Account", key: "debitAccount" }
  ];

  const ExportCSV = ({ data }) => (
    <CSVLink data={data} headers={headers} filename="payment-receive-entries.csv">
      <button className="btn btn-primary mt-1" id="export">
        Export
      </button>
      <UncontrolledTooltip placement="top" target="export">
        Export to CSV
      </UncontrolledTooltip>
    </CSVLink>
  );

  return (
    <Fragment>
      <Breadcrumb parent="Payment Receive Entries" title="List Payment Receive Entries" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                  <h5>List Payment Receive Entries</h5>
                  <Link to={`/ipd/transaction/payment-receive-entry/add`}>
                    <button className="btn btn-primary" id="create">
                      Create New
                    </button>
                    <UncontrolledTooltip placement="top" target="create">
                      Create New Payment Receive Entry
                    </UncontrolledTooltip>
                  </Link>
                </div>

                <div className="row mt-2">
                  <div className="col-sm-6">
                    <input
                      className="form-control"
                      type="text"
                      placeholder="Search by patient, doctor, or IPD No..."
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                    />
                  </div>
                  <div className="col-sm-6 text-end">
                    <ExportCSV data={data} />
                  </div>
                </div>
              </div>

              <div className="card-body">
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination
                  highlightOnHover
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default ListPaymentReceiveEntry;
